import React, { useEffect, useRef } from "react";

interface ActionDropdownProps {
  options: { label: string; icon: React.ReactNode; onClick: () => void }[];
  isOpen: boolean;
  onClose: () => void;
  toggleRef?: React.RefObject<HTMLButtonElement | null>; // Reference to the toggle button
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({
  options,
  isOpen,
  onClose,
  toggleRef,
}) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if the click is outside both the dropdown and the toggle button
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        toggleRef?.current &&
        !toggleRef.current.contains(target)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, toggleRef]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="position-absolute end-0 d-flex flex-column dropdown-menu"
      ref={dropdownRef}
      onClick={(event) => event.stopPropagation()} // Prevent closing when clicking inside
      onMouseDown={(event) => event.stopPropagation()} 
    >
      {options.map((option, index) => (
        <div
          key={index}
          className="dropdown-item cursor-pointer text-nowrap"
          onClick={() => {
            option.onClick();
            onClose(); // Close dropdown after an option is clicked
          }}
        >
          {option.icon}
          {option.label}
        </div>
      ))}
    </div>
  );
};

export default ActionDropdown;